import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, CheckCircle2, XCircle, Zap, Settings, Clock,
  Download, Printer, Package, ChevronRight
} from "lucide-react";

const todayIso = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

const ShiftSummary = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/shift-summary?date=${date}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [date]);

  const exportCsv = () => {
    if (!data?.machines?.length) return;
    const header = ["Machine", "Jobs", "OK", "NG", "Strokes", "Setup Strokes", "Downtime (min)", "Downtime Events", "Open Job"];
    const rows = data.machines.map((m) => [
      m.machine_code, m.jobs_count, m.ok_qty, m.ng_qty, m.strokes,
      m.setup_strokes_total, m.downtime_minutes, m.downtime_events, m.open_job_id || ""
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shift-summary-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rejPct = data?.totals?.rejection_pct || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 px-3 py-2 rounded-lg"
            >
              <Printer size={14} /> Print
            </button>
            <button
              data-testid="shift-summary-export"
              onClick={exportCsv}
              disabled={!data?.machines?.length}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-3 py-2 rounded-lg"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#1A3560] to-[#2d4a7d] text-white rounded-2xl p-6 shadow-lg mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest opacity-80">Shift-end Summary</p>
              <h1 className="text-3xl font-extrabold">{new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h1>
            </div>
            <input
              data-testid="shift-summary-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-white/10 border border-white/30 rounded-lg px-3 py-2 text-white text-sm print:hidden"
            />
          </div>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <KpiCard label="OK Parts" value={data.totals.ok_qty} icon={<CheckCircle2 size={16} />} color="emerald" />
            <KpiCard label="NG Parts" value={data.totals.ng_qty} icon={<XCircle size={16} />} color="red" />
            <KpiCard label="Rejection %" value={`${rejPct}%`} icon={<XCircle size={16} />} color={rejPct > 5 ? "red" : rejPct > 2 ? "amber" : "emerald"} />
            <KpiCard label="Strokes" value={data.totals.strokes} icon={<Zap size={16} />} color="slate" />
            <KpiCard label="Setup Strokes" value={data.totals.setup_strokes} icon={<Settings size={16} />} color="amber" />
            <KpiCard label="Downtime" value={`${data.totals.downtime_minutes}m`} icon={<Clock size={16} />} color="red" sub={`${data.totals.downtime_events} events`} />
          </div>
        )}

        {/* Machines table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
              Per-machine breakdown <span className="ml-2 text-blue-600 font-medium normal-case tracking-normal">· click any row for hourly deep-dive</span>
            </p>
            {data && (
              <p className="text-xs text-slate-400">
                {data.totals.machines_active} of {data.totals.total_machines} machines active
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left">Machine</th>
                  <th className="px-4 py-2 text-left">Open Job</th>
                  <th className="px-4 py-2 text-right">Jobs</th>
                  <th className="px-4 py-2 text-right">OK</th>
                  <th className="px-4 py-2 text-right">NG</th>
                  <th className="px-4 py-2 text-right">Strokes</th>
                  <th className="px-4 py-2 text-right">Setup</th>
                  <th className="px-4 py-2 text-right">Downtime (min)</th>
                  <th className="px-4 py-2 text-right">DT Events</th>
                </tr>
              </thead>
              <tbody data-testid="shift-summary-tbody">
                {loading ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-8">Loading...</td></tr>
                ) : !data?.machines?.length ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-8">No machines found</td></tr>
                ) : data.machines.map((m) => {
                  const active = m.strokes > 0 || m.downtime_events > 0 || m.setup_strokes_total > 0;
                  return (
                    <tr
                      key={m.machine_code}
                      onClick={() => navigate(`/admin/machine-deep-dive/${m.machine_code}?date=${date}`)}
                      data-testid={`shift-row-${m.machine_code}`}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-blue-50 transition-colors ${active ? "" : "opacity-60"}`}
                      title="Click for hourly deep-dive"
                    >
                      <td className="px-4 py-2 font-bold text-slate-900">
                        <span className="inline-flex items-center gap-1">
                          <Package size={12} className="text-slate-400" /> {m.machine_code}
                          <span className="text-xs text-slate-400 ml-1">({m.tonnage}T)</span>
                          <ChevronRight size={12} className="text-slate-300 ml-1" />
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {m.open_job_id ? (
                          <span title={`Tool ${m.open_job_tool} · ${m.open_job_part}`}>
                            {m.open_job_id}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">{m.jobs_count}</td>
                      <td className="px-4 py-2 text-right font-extrabold text-emerald-700">{m.ok_qty}</td>
                      <td className="px-4 py-2 text-right font-extrabold text-red-600">{m.ng_qty}</td>
                      <td className="px-4 py-2 text-right">{m.strokes}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={m.setup_strokes_total > 0 ? "font-extrabold text-amber-700" : "text-slate-400"}>
                          {m.setup_strokes_total}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={m.downtime_minutes > 0 ? "font-extrabold text-red-600" : "text-slate-400"}>
                          {m.downtime_minutes}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {m.downtime_events > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-slate-600">{m.downtime_events}</span>
                            {m.downtime_pending > 0 && (
                              <span className="text-[10px] font-extrabold text-red-600 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded-full">
                                {m.downtime_pending} pending
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon, color, sub }) => {
  const map = {
    emerald: "text-emerald-700 bg-emerald-100",
    red: "text-red-700 bg-red-100",
    amber: "text-amber-700 bg-amber-100",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${map[color] || map.slate}`}>{icon}</div>
      </div>
      <p className="text-2xl font-extrabold text-slate-900 truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
};

export default ShiftSummary;
